import React, { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '#app/hooks/useAppStore';
import { handleCloseModal, handleForgotModal, login } from '../../actions/auth';
import { Container, HeaderForget, HeaderForgetContainer, Wrapper } from './styles';
import { Row } from '../styles';
import { Button, Input, InputContainer } from 'finform';
import { NotifyManager } from 'notify-component';
import LocalStorageHelper from '#app/helpers/Cache/LocalStorageHelper';
import { useLoginForm } from './hooks/useLoginForm';
import {
  shouldShowFieldError,
  getFieldErrorFromMeta,
} from '#app/utils/tanstackFormErrorVisibility';

export interface LoginProps {
  callback?: () => void;
}

function Login({ callback }: LoginProps = {}) {
  const dispatch = useAppDispatch();
  const i18n = useAppSelector((s) => s.i18n) as any;
  const initialEmail = localStorage.getItem(LocalStorageHelper.AUTH_EMAIL) || '';
  const [submitted, setSubmitted] = useState(false);
  const [type, setType] = useState('');

  const handleCloseModalFn = useCallback(() => dispatch(handleCloseModal() as any), [dispatch]);
  const handleForgotModalFn = useCallback(() => dispatch(handleForgotModal() as any), [dispatch]);
  const loginFn = useCallback((payload: any) => dispatch((login as any)(payload)), [dispatch]);

  const { tanstackForm, handleSubmit } = useLoginForm({
    initialEmail,
    requireTwoFactorCode: type === '2fa',
    onSubmit: (values, obj) => {
      setSubmitted(true);
      loginFn({
        form: values,
        obj,
        success: (_response: unknown, _status: unknown, xhr: XMLHttpRequest) => {
          const header = xhr.getResponseHeader('Authorization');
          if (header) {
            localStorage.setItem(LocalStorageHelper.API_TOKEN, header);
            handleCloseModalFn();
            tanstackForm.reset();
            setSubmitted(false);
            if (typeof callback === 'function') {
              callback();
            }
          } else {
            NotifyManager.error(
              'Ошибка авторизации',
              i18n?.auth?.authError ?? 'Ошибка авторизации',
              5000
            );
          }
        },
        error: (_xhr: unknown, responseData: unknown) => {
          const data = responseData as
            | {
                meta?: { errors?: { code?: string[] }; message?: string };
              }
            | undefined;
          setSubmitted(false);
          if (type === '' && data?.meta?.errors?.code?.[0]) {
            const enable = data.meta.errors.code[0] === 'Неверный 2FA код';
            setType(enable ? '2fa' : '');
          }
          // Если нет полевых ошибок но есть общее сообщение — показываем тост
          if (!data?.meta?.errors && data?.meta?.message) {
            NotifyManager.error('Ошибка', data.meta.message, 5000);
          }
        },
      });
    },
  });

  const handleEnter = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <Wrapper>
      <Container>
        <tanstackForm.Field name="email">
          {(field) => (
            <Row>
              <InputContainer
                error={
                  shouldShowFieldError(field.state.meta, tanstackForm.state)
                    ? getFieldErrorFromMeta(field.state.meta)
                    : undefined
                }
              >
                <Input
                  id="login-email"
                  name="email"
                  onKeyDown={handleEnter}
                  autoComplete="on"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                  onBlur={field.handleBlur}
                  placeholder={i18n?.auth?.email ?? 'Email'}
                />
              </InputContainer>
            </Row>
          )}
        </tanstackForm.Field>

        <tanstackForm.Field name="password">
          {(field) => (
            <Row>
              <InputContainer
                error={
                  shouldShowFieldError(field.state.meta, tanstackForm.state)
                    ? getFieldErrorFromMeta(field.state.meta)
                    : undefined
                }
              >
                <Input
                  id="login-password"
                  type="password"
                  name="password"
                  onKeyDown={handleEnter}
                  value={field.state.value}
                  onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                  onBlur={field.handleBlur}
                  placeholder={i18n?.auth?.password ?? 'Пароль'}
                />
              </InputContainer>
            </Row>
          )}
        </tanstackForm.Field>

        {type === '2fa' && (
          <tanstackForm.Field name="code">
            {(field) => (
              <Row>
                <InputContainer
                  error={
                    shouldShowFieldError(field.state.meta, tanstackForm.state)
                      ? getFieldErrorFromMeta(field.state.meta)
                      : undefined
                  }
                >
                  <Input
                    id="login-code"
                    type="code"
                    name="code"
                    value={field.state.value || ''}
                    onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                    onBlur={field.handleBlur}
                    placeholder={i18n?.auth?.fa ?? 'Код'}
                  />
                </InputContainer>
              </Row>
            )}
          </tanstackForm.Field>
        )}

        <Row>
          <HeaderForgetContainer>
            <HeaderForget
              id="forgot-button"
              onClick={(e) => {
                e.preventDefault();
                handleForgotModalFn();
              }}
            >
              {i18n?.auth?.forgotPassword ?? 'Забыли пароль?'}
            </HeaderForget>
          </HeaderForgetContainer>
        </Row>
        <Row>
          <Button wide id="login-submit" onClick={handleSubmit} disabled={submitted}>
            {i18n?.auth?.enter ?? 'Войти'}
          </Button>
        </Row>
      </Container>
    </Wrapper>
  );
}

export { Login };
export default Login;
